// Library methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import FormField from "../FormField/FormField";
import InfoNote from "../InfoNote/InfoNote";
import PacServerJobsForm, {
  AddressFields,
} from "../PacServerJobsForm/PacServerJobsForm";

// Utils
import {
  PacServerFormValues,
  buildDicomAddress,
  buildPacServerJobs,
  getDeviceDisplayName,
  needsWorklist,
  pacServerFormValuesFromServer,
  remoteAeTitleLabel,
} from "src/utils/emr/pacServerHelper";

// Hooks
import { useUpdateEMRPacServer } from "src/services/emr/hooks/emr-pac-servers/useUpdateEMRPacServer";
import { useDeleteEMRPacServer } from "src/services/emr/hooks/emr-pac-servers/useDeleteEMRPacServer";

// Types
import { EMRCatalogDeviceType, EMRPacServerType } from "src/services/emr/types";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  ModalCustomDelete,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

type EditPacServerModalType = {
  open: boolean;
  clinicId: string | undefined;
  hubId: string | undefined;
  pacServer: EMRPacServerType;
  device: EMRCatalogDeviceType | undefined;
  listenPort: string;
  hubHasOtherWorklist: boolean;
  onClose: () => void;
};

const EditPacServerModal = ({
  open,
  clinicId,
  hubId,
  pacServer,
  device,
  listenPort,
  hubHasOtherWorklist,
  onClose,
}: EditPacServerModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);

  const initialValues = useMemo(
    () => pacServerFormValuesFromServer(pacServer, device, listenPort),
    [pacServer, device, listenPort]
  );
  const [values, setValues] = useState<PacServerFormValues>(initialValues);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);

  useEffect(() => {
    if (open) setValues(initialValues);
  }, [open, initialValues]);

  const { mutate: updatePacServer, isPending: isUpdating } =
    useUpdateEMRPacServer(clinicId, hubId);
  const { mutate: deletePacServer, isPending: isDeleting } =
    useDeleteEMRPacServer(clinicId, hubId);

  const jobs = useMemo(
    () => buildPacServerJobs(values, device),
    [values, device]
  );

  const addressChanged =
    JSON.stringify(values.address) !== JSON.stringify(initialValues.address);
  const ownAddressJobs = [
    values.worklist.connectionId && !values.worklist.useServerAddress
      ? t("Worklist")
      : undefined,
    values.exams.connectionId && !values.exams.useServerAddress
      ? t("Exams")
      : undefined,
    values.reports.connectionId && !values.reports.useServerAddress
      ? t("Reports")
      : undefined,
  ].filter(Boolean);

  const isValid =
    values.name.trim().length > 0 &&
    buildDicomAddress(values.address) !== null &&
    jobs !== null &&
    !needsWorklist(values, hubHasOtherWorklist);

  const handleSave = useCallback(() => {
    if (!jobs) return;

    updatePacServer(
      {
        pacServerId: pacServer.pacServerId,
        name: values.name.trim(),
        deviceKey: values.deviceKey,
        jobs,
      },
      { onSuccess: onClose }
    );
  }, [jobs, pacServer.pacServerId, values, updatePacServer, onClose]);

  return (
    <>
      <DefaultModal
        isOpen={open}
        onClose={onClose}
        title={`${t("Edit")} ${pacServer.name}`}
        maxWidth="sm"
        modalActions={
          <Grid
            sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <Button sx={ModalCustomCancel} onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              sx={ModalCustomConfirm}
              disabled={!isValid || isUpdating}
              onClick={handleSave}
            >
              {t("Save changes")}
            </Button>
          </Grid>
        }
      >
        <Box display="flex" flexDirection="column" gap="16px" p="16px">
          <Box display="flex" flexDirection="column" gap="10px">
            <Box>
              <Typography fontSize="15px" fontWeight={800}>
                {t("PAC server address")}
              </Typography>
              <Typography fontSize="12px" color={mutedText}>
                {getDeviceDisplayName(device) || pacServer.deviceKey}
              </Typography>
            </Box>
            <AddressFields
              idPrefix="edit-pac-server"
              value={values.address}
              disabled={false}
              remoteAeLabel={remoteAeTitleLabel(device, t)}
              showHost
              onChange={(address) => setValues({ ...values, address })}
            />
            {addressChanged && (
              <InfoNote testId="edit-pac-server-address-changed" tone="warning">
                {ownAddressJobs.length > 0
                  ? `${t("Saving updates the jobs that use the PAC server's address.")} ${t(
                      "{{jobs}} has its own address and is not touched.",
                      { jobs: ownAddressJobs.join(", ") }
                    )}`
                  : t(
                      "Saving updates the jobs that use the PAC server's address."
                    )}
              </InfoNote>
            )}
            <FormField
              label={t("Name")}
              htmlFor="edit-pac-server-name-input"
              required
            >
              <TextInputField
                id="edit-pac-server-name-input"
                placeholder={t("e.g. Main PAC server")}
                value={values.name}
                maxLength={100}
                onChange={(event) =>
                  setValues({ ...values, name: event.target.value })
                }
              />
            </FormField>
          </Box>

          <Box display="flex" flexDirection="column" gap="10px">
            <Typography fontSize="15px" fontWeight={800}>
              {t("Jobs")}
            </Typography>
            <PacServerJobsForm
              idPrefix="edit-pac-server"
              values={values}
              device={device}
              hubHasOtherWorklist={hubHasOtherWorklist}
              onChange={setValues}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            flexWrap="wrap"
            sx={{ borderTop: "1px solid #6E7186", paddingTop: "14px" }}
          >
            <Typography fontSize="12px" color={mutedText}>
              {t(
                "Removing the PAC server stops all its jobs. Exams already brought in stay."
              )}
            </Typography>
            <Button
              sx={ModalCustomDelete(theme)}
              onClick={() => setOpenRemoveModal(true)}
            >
              {t("Remove PAC server")}
            </Button>
          </Box>
        </Box>
      </DefaultModal>

      <SharedModal
        title={t("Remove PAC server?")}
        modalDescription={t("This stops its worklist, exams and reports jobs.")}
        confirmButtonText={t("Remove")}
        confirmButtonVariant="error"
        isDisabledButton={isDeleting}
        open={openRemoveModal}
        onClose={() => setOpenRemoveModal(false)}
        onConfirm={() =>
          deletePacServer(pacServer.pacServerId, {
            onSuccess: () => {
              setOpenRemoveModal(false);
              onClose();
            },
          })
        }
      />
    </>
  );
};

export default EditPacServerModal;
